// ── Project-scope nav groups ─────────────────────────────────────────────
// Ports Kalligo's project-sidebar taxonomy (kalligo-dashboard's
// src/constants/sidebarNavGroups.ts) onto Ans's existing view ids, so the
// two apps' menus read as the same product: same section names, same
// order, same labels, same child grouping.
//
// Section order (matches Kalligo exactly):
//   PLANNING -> RELATIONS -> VENDORS -> STYLE -> LOGISTICS -> FINANCE
//   -> DAY MANAGEMENT -> footer (History, Settings, Support)
//
// ── Module flag registry (the single merge point for other agents) ──────
// Every item below is gated on a flag from `../constants/featureFlags`.
// A module whose screen doesn't exist yet in Ans is simply left OUT of its
// array (never rendered as a disabled "Soon" row) — add it back with one
// line once both the flag and the screen exist. Table of who owns what:
//
//   Flag                          Section   Item                  Status
//   ───────────────────────────── ───────── ───────────────────── ───────
//   GUESTS_ENABLED                Relations Guests                built (list, form, profile, merge, bulk add)
//   TEAM_ENABLED                  Relations Team & Contacts        built (#502 — roster, merged logins, form)
//   VENDORS_ENABLED               Vendors   Vendors                built
//   VENDOR_BOOKINGS_ENABLED       Vendors   Vendor Bookings        built (list, booking page, new booking from an accepted quote)
//   PROCUREMENT_ENABLED           Vendors   Procurement (parent)   built (Orders, Shipments, Shipping Agents, Inventory)
//     procurement-orders          Vendors   Procurement > Orders   built
//     procurement-shipments       Vendors   Procurement > Shipments built
//     shipping                    Vendors   Procurement > Shipping Agents built
//     procurement-inventory       Vendors   Procurement > Inventory built
//     procurement-products        Vendors   Procurement > Products pending (no screen)
//   CATALOGUE_ENABLED             Style     Catalogue              built
//   ASOEBI_ENABLED                Style     Asoebi & Style (parent) built (Fabrics, Orders, Bundles)
//     asoebi-fabrics              Style     Asoebi > Fabrics       built
//     asoebi-assignments          Style     Asoebi > Assignments   pending (no packing API on port/kal yet)
//     asoebi-deliveries           Style     Asoebi > Deliveries    pending (API live; screen needs order-line-level delivery linking)
//     asoebi-orders               Style     Asoebi > Orders        built
//   GIFT_REGISTRY_ENABLED         Style     Gift Registry          built (list, item page, share link; guests reserve on the public page)
//   MENU_ENABLED                  Style     Menu                   built
//   MUSIC_ENABLED                 Style     Music                  built
//   STATIONERY_ENABLED            Style     Stationery & Branding  built
//   DECOR_ENABLED                 Style     Decor Planner          built
//   VENUES_ENABLED                Logistics Venues & Floor Plans   built
//   LOGISTICS_ENABLED             Logistics Logistics (parent)     built (Vehicles, Drivers, Schedule)
//     logistics-assignments       Logistics Logistics > Transport Assignments pending (no screen)
//   BUDGET_ENABLED                Finance   Budget, Budget Variances built
//   QUOTES_ENABLED                Finance   Quotes                 built
//   PAYMENTS_ENABLED              Finance   Payments               built
//   EVENT_DAY_ENABLED             Day Mgmt  Event Day Ops          built
//   GUIDELINES_ENABLED            Day Mgmt  Guidelines             built
//   SUPPORT_ENABLED                footer    Support                pending (no screen)
//
// A PR adding e.g. Guests only has to: add a Guests screen + route, flip
// GUESTS_ENABLED on in featureFlags.ts, and add one `{ id: 'guests', ... }`
// line to RELATIONS_NAV_ITEMS below — nothing else in this file changes.
//
// Role gating uses Ans's own roleLevel()/minRole scheme (utils/roles.ts),
// not Kalligo's — Ans has no 'admin' tier distinct from 'planner' in the
// sidebar today, so minRole stays 'viewer' | 'planner' | 'owner' as the
// existing Sidebar.tsx already used. Money items (Budget/Payments/Quotes/
// Budget Variances) are NOT gated here — Sidebar.tsx applies the separate
// canViewFinancials() check on top, same as before this port.

export interface MenuItem {
    id: string;
    icon: string;
    label: string;
    disabled?: boolean;
    badge?: number | string;
    /** Minimum role required to interact with this item. */
    minRole?: 'viewer' | 'planner' | 'admin' | 'owner';
    /** Children rendered nested under this item. They inherit the parent's
     *  moduleKey/flag gate and minRole — they never render when the parent
     *  doesn't. */
    children?: MenuItem[];
}

// Planning — Dashboard, Tasks, Events, Calendar, Day Timeline.
export const PLANNING_NAV_ITEMS: MenuItem[] = [
    { id: 'dashboard', icon: 'chart', label: 'Dashboard', minRole: 'viewer' },
    { id: 'activities', icon: 'check', label: 'Tasks', minRole: 'viewer' },
    { id: 'events', icon: 'calendar', label: 'Events', minRole: 'viewer' },
    { id: 'calendar', icon: 'grid', label: 'Calendar', minRole: 'viewer' },
    { id: 'timeline', icon: 'timeline', label: 'Day Timeline', minRole: 'viewer' },
];

// Relations — Communications, Guests, Team & Contacts.
export const RELATIONS_NAV_ITEMS: MenuItem[] = [
    // Guests: list, form, profile, merge duplicates, bulk add — built on the kal shape, gated by GUESTS_ENABLED.
    { id: 'guests', icon: 'users', label: 'Guests', minRole: 'viewer' },
    { id: 'communications', icon: 'message', label: 'Communications', minRole: 'planner' },
    // Team roster lists staff e-mails and phones, served by the API to
    // admins and owners only (#502) — same minRole the API enforces.
    { id: 'team', icon: 'users', label: 'Team & Contacts', minRole: 'admin' },
];

// Vendors — Vendors, Vendor Bookings, Procurement > Orders/Inventory/
// Products/Shipments/Shipping/Shipping Agents.
export const VENDORS_NAV_ITEMS: MenuItem[] = [
    { id: 'vendors', icon: 'briefcase', label: 'Vendors', minRole: 'viewer' },
    { id: 'vendor-bookings', icon: 'document', label: 'Vendor Bookings', minRole: 'viewer' },
    {
        // Viewers read; planners write — Procurement is not money-only, so
        // it stays visible to a financially-blind contractor too.
        id: 'procurement', icon: 'globe', label: 'Procurement', minRole: 'viewer',
        children: [
            // Products: no Ans screen yet.
            { id: 'procurement-orders', icon: 'package', label: 'Orders', minRole: 'viewer' },
            { id: 'procurement-shipments', icon: 'truck', label: 'Shipments', minRole: 'viewer' },
            { id: 'shipping', icon: 'ship', label: 'Shipping Agents', minRole: 'viewer' },
            { id: 'procurement-inventory', icon: 'grid', label: 'Inventory', minRole: 'viewer' },
        ],
    },
];

// Style — Catalogue, Asoebi & Style, Gift Registry, Menu, Music,
// Stationery & Branding, Decor Planner.
export const STYLE_NAV_ITEMS: MenuItem[] = [
    { id: 'catalogue', icon: 'grid', label: 'Catalogue', minRole: 'planner' },
    {
        id: 'asoebi', icon: 'sparkles', label: 'Asoebi & Style', minRole: 'planner',
        children: [
            // Assignments (packing checklist) and Deliveries: not wired to a screen yet, left out.
            { id: 'asoebi-fabrics', icon: 'list', label: 'Fabrics', minRole: 'planner' },
            { id: 'asoebi-orders', icon: 'package', label: 'Orders', minRole: 'planner' },
            { id: 'asoebi-bundles', icon: 'gift', label: 'Bundles', minRole: 'planner' },
        ],
    },
    // Viewers read the list; planners and above change it and manage the guests' share link.
    { id: 'gift-registry', icon: 'gift', label: 'Gift Registry', minRole: 'viewer' },
    { id: 'menu', icon: 'utensils', label: 'Menu', minRole: 'viewer' },
    { id: 'music', icon: 'music', label: 'Music', minRole: 'viewer' },
    { id: 'stationery', icon: 'document', label: 'Stationery & Branding', minRole: 'planner' },
    { id: 'decor', icon: 'palette', label: 'Decor Planner', minRole: 'planner' },
];

// Logistics — Venues & Floor Plans; Logistics > Vehicles, Drivers,
// Transport Assignments, Schedule.
export const LOGISTICS_NAV_ITEMS: MenuItem[] = [
    { id: 'venue', icon: 'map', label: 'Venues & Floor Plans', minRole: 'planner' },
    {
        id: 'logistics', icon: 'mapPin', label: 'Logistics', minRole: 'planner',
        children: [
            { id: 'logistics-vehicles', icon: 'truck', label: 'Vehicles', minRole: 'planner' },
            { id: 'logistics-drivers', icon: 'user', label: 'Drivers', minRole: 'planner' },
            // Transport Assignments: no Ans screen yet.
            { id: 'logistics-schedule', icon: 'clock', label: 'Schedule', minRole: 'planner' },
        ],
    },
];

// Finance — Budget, Quotes, Payments, Budget Variances. Money items are
// additionally gated on canViewFinancials() by Sidebar.tsx, not here.
export const FINANCE_NAV_ITEMS: MenuItem[] = [
    { id: 'budget', icon: 'dollar', label: 'Budget', minRole: 'viewer' },
    { id: 'quotes', icon: 'document', label: 'Quotes', minRole: 'viewer' },
    {
        id: 'payments', icon: 'creditCard', label: 'Payments', minRole: 'viewer',
        children: [
            { id: 'payments-contributors', icon: 'users', label: 'Contributors', minRole: 'viewer' },
            { id: 'payments-settlement', icon: 'briefcase', label: 'Settlement', minRole: 'viewer' },
        ],
    },
    { id: 'budget-variances', icon: 'alert', label: 'Budget Variances', minRole: 'viewer' },
];

// Day Management — Event Day Ops, Guidelines.
export const DAY_MANAGEMENT_NAV_ITEMS: MenuItem[] = [
    { id: 'event-day', icon: 'flag', label: 'Event Day Ops', minRole: 'planner' },
    { id: 'guidelines', icon: 'list', label: 'Guidelines', minRole: 'planner' },
];

// Footer — History, Settings. Support has no Ans screen yet (pending
// SUPPORT_ENABLED in the registry above).
export const ADMIN_NAV_ITEMS: MenuItem[] = [
    { id: 'history', icon: 'clock', label: 'History', minRole: 'planner' },
    { id: 'settings', icon: 'settings', label: 'Settings', minRole: 'owner' },
];

/** Kalligo's project-sidebar section order and titles — the contract test
 *  (Sidebar.taxonomy.test.tsx) asserts a full-permission, all-flags-on
 *  render's visible section/label order equals this exactly. */
export const KALLIGO_TAXONOMY_SECTIONS: Array<{ title: string; labels: string[] }> = [
    { title: 'Planning', labels: PLANNING_NAV_ITEMS.map(i => i.label) },
    { title: 'Relations', labels: RELATIONS_NAV_ITEMS.map(i => i.label) },
    { title: 'Vendors', labels: VENDORS_NAV_ITEMS.map(i => i.label) },
    { title: 'Style', labels: STYLE_NAV_ITEMS.map(i => i.label) },
    { title: 'Logistics', labels: LOGISTICS_NAV_ITEMS.map(i => i.label) },
    { title: 'Finance', labels: FINANCE_NAV_ITEMS.map(i => i.label) },
    { title: 'Day Management', labels: DAY_MANAGEMENT_NAV_ITEMS.map(i => i.label) },
];
